/**
 * Plan recommendation — the published deployment standards applied to the
 * organization's own headcount (2026-08-13 owner directive).
 *
 * The org-size band is captured as account metadata during onboarding/trial
 * (users/{uid}.organization_employee_count, written by the wallet profile
 * card) and determines the highest recommended rung:
 *   - 40+ human employees → 5 Prest0ns minimum (annual · 5+ team, $2,750)
 *   - 20–39               → 3 Prest0ns minimum (annual · 3+ team, $2,950)
 *   - under 20            → 1 Prest0n (annual hire, $3,200)
 * The minimums are deployment standards, not sales tiers — "we don't deploy
 * under-staffed" (published pricing sheet).
 *
 * Trial pricing reveal: in-app upgrade offers stay quiet for trial accounts
 * until they have actually used $25 of PRO Work Units — after that, the
 * recommendation surfaces (owner directive 2026-08-14: lowered from $100 so
 * the upgrade path opens once real work has demonstrably flowed).
 */

import { WU_PRICING, MONTHLY_WU_PER_PRESTON } from './workUnits';

export type OrgSizeBand = '1-4' | '5-19' | '20-39' | '40+';

export const ORG_SIZE_BANDS: Array<{ value: OrgSizeBand; label: string }> = [
  { value: '1-4', label: '1–4 people' },
  { value: '5-19', label: '5–19 people' },
  { value: '20-39', label: '20–39 people' },
  { value: '40+', label: '40 or more people' },
];

/** Wallet-usage threshold (USD of PRO Work Units) that reveals in-app plan offers to trial accounts. */
export const TRIAL_PRICING_REVEAL_USD = 25;

export interface PlanRecommendation {
  /** Backend tier id (functions/src/subscriptions.ts TIER_CONFIG). */
  tier: 'wu_annual' | 'wu_annual_team3' | 'wu_annual_team5';
  /** Recommended number of Prest0ns — deliberately ABOVE the floor where the
      org has room (task #70): the recommendation is what serves the team well;
      the user can step down to minSeats. */
  seats: number;
  /** The floor for this org size — the stepper never goes below this. */
  minSeats: number;
  /** Whether the seat count is a published deployment minimum for this org size. */
  minimumRequired: boolean;
  /** Per-Prest0n monthly-equivalent price in USD. */
  monthlyUsdPerSeat: number;
  /** Per-PR0 committed rate at this rung. */
  usdPerWu: number;
  /** Whole-team monthly-equivalent total. */
  totalMonthlyUsd: number;
  /** Whole-team PRO Work Units per month. */
  totalWuMonthly: number;
  /** Savings vs the Standard Rate, published ladder figure. */
  savePct: number;
  /** One-line reason shown with the offer. */
  reason: string;
}

export function recommendPlan(band?: OrgSizeBand | string | null): PlanRecommendation {
  if (band === '40+') {
    return {
      tier: 'wu_annual_team5',
      seats: 6,
      minSeats: 5,
      minimumRequired: true,
      monthlyUsdPerSeat: WU_PRICING.annualTeam5.monthlyUsd,
      usdPerWu: WU_PRICING.annualTeam5.usdPerWu,
      totalMonthlyUsd: WU_PRICING.annualTeam5.monthlyUsd * 6,
      totalWuMonthly: MONTHLY_WU_PER_PRESTON * 6,
      savePct: WU_PRICING.annualTeam5.savePct,
      // Task #70: lead with the best offer, never a size justification.
      reason: 'our best published rate — full coverage for your team.',
    };
  }
  if (band === '20-39') {
    return {
      tier: 'wu_annual_team3',
      seats: 4,
      minSeats: 3,
      minimumRequired: true,
      monthlyUsdPerSeat: WU_PRICING.annualTeam3.monthlyUsd,
      usdPerWu: WU_PRICING.annualTeam3.usdPerWu,
      totalMonthlyUsd: WU_PRICING.annualTeam3.monthlyUsd * 4,
      totalWuMonthly: MONTHLY_WU_PER_PRESTON * 4,
      savePct: WU_PRICING.annualTeam3.savePct,
      reason: 'the team rate — coverage that keeps up with your caseload.',
    };
  }
  return {
    tier: 'wu_annual',
    seats: band === '5-19' ? 2 : 1,
    minSeats: 1,
    minimumRequired: false,
    monthlyUsdPerSeat: WU_PRICING.annual.monthlyUsd,
    usdPerWu: WU_PRICING.annual.usdPerWu,
    totalMonthlyUsd: WU_PRICING.annual.monthlyUsd * (band === '5-19' ? 2 : 1),
    totalWuMonthly: MONTHLY_WU_PER_PRESTON * (band === '5-19' ? 2 : 1),
    savePct: WU_PRICING.annual.savePct,
    reason: 'the annual hire — the rate most companies choose.',
  };
}

/**
 * Should in-app plan offers render for this viewer? Non-trial accounts:
 * always (permission gates still apply). Trial accounts: only after $25 of
 * PRO Work Units has actually been used — EXCEPT once the trial window has
 * ended (owner decision 8, 2026-08-16): a lapsed-trial owner must always be
 * able to see and buy the plans, whatever they spent. Hiding the upgrade
 * from the very account that needs it would strand them.
 */
export function shouldRevealPlanOffers(opts: {
  isTrial: boolean;
  totalSpentUsd: number | null | undefined;
  /** True when the trial window has expired (trialStatus.isExpired). */
  trialEnded?: boolean;
}): boolean {
  if (!opts.isTrial) return true;
  if (opts.trialEnded === true) return true;
  return (opts.totalSpentUsd ?? 0) >= TRIAL_PRICING_REVEAL_USD;
}
